import React, { useMemo, useState } from 'react';
import i18n from 'i18next';
import styles from './ParticipantMapSheet.module.css';
import { IconUsers } from 'components/ds';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface ParticipantRowInfo {
    userNo: number;
    nickname: string;
    profileImgUrl?: string;
    attendanceStatus?: string; // ATTENDING | NOT_ATTENDING | UNDECIDED
    locationSharing?: boolean;
    isHost?: boolean;
    isMe?: boolean;
}

export interface ParticipantMapSheetProps {
    participants: ParticipantRowInfo[];
    /** 시트 상태 변화 콜백 (부모가 RaceBottomSheet 위치 조정용) */
    onHeightChange?: (heightPx: number) => void;
    /** 외부에서 강제 펼침/접기 제어하려면 */
    defaultExpanded?: boolean;
}

/* 인라인 SVG — 이모지 금지 */

const IcPin = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const IcPinOff = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <line x1="4" y1="4" x2="20" y2="20" />
    </svg>
);

const IcChevron = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const IcCrown = () => (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 17h18l-2-9-4 3-3-5-3 5-4-3z" />
    </svg>
);

const ATTENDANCE_LABEL: Record<string, string> = {
    ATTENDING: i18n.t('meeting.attending'),
    NOT_ATTENDING: i18n.t('meeting.notAttending'),
    UNDECIDED: i18n.t('meeting.undecided'),
};

export const PARTICIPANT_SHEET_COLLAPSED_PX = 92;
export const PARTICIPANT_SHEET_EXPANDED_PX = 360;

const ParticipantMapSheet: React.FC<ParticipantMapSheetProps> = ({
    participants,
    onHeightChange,
    defaultExpanded = false,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const sorted = useMemo(() => {
        // 정렬: 나 → 주최자 → 참석 → 미정 → 불참
        const rank = (p: ParticipantRowInfo) => {
            if (p.isMe) return 0;
            if (p.isHost) return 1;
            if (p.attendanceStatus === 'ATTENDING') return 2;
            if (p.attendanceStatus === 'UNDECIDED') return 3;
            return 4;
        };
        return [...participants].sort((a, b) => rank(a) - rank(b));
    }, [participants]);

    const attendingCount = participants.filter(p => p.attendanceStatus === 'ATTENDING').length;
    const total = participants.length;

    const toggle = () => {
        const next = !expanded;
        setExpanded(next);
        onHeightChange?.(next ? PARTICIPANT_SHEET_EXPANDED_PX : PARTICIPANT_SHEET_COLLAPSED_PX);
    };

    // 접힘 상태일 때 한 줄만 보이게 (나 우선, 없으면 첫 번째)
    const collapsedTarget = sorted.find(p => p.isMe) || sorted[0] || null;

    return (
        <div className={cls(`pms-sheet ${expanded ? 'expanded' : 'collapsed'}`)}>
            {/* 핸들 + 요약 */}
            <button className={cls("pms-handle")} onClick={toggle} aria-expanded={expanded}>
                <span className={cls("pms-handle-bar")} />
                <div className={cls("pms-handle-main")}>
                    <span className={cls("pms-handle-icon")}><IconUsers /></span>
                    <span className={cls("pms-handle-title")}>{i18n.t('mapUi.participants')}</span>
                    <span className={cls("pms-handle-count")}>{attendingCount}/{total}명 참석</span>
                </div>
                <span className={cls(`pms-handle-chevron ${expanded ? 'open' : ''}`)}><IcChevron /></span>
            </button>

            {/* 접힘 상태 요약 행 */}
            {!expanded && collapsedTarget && (
                <div className={cls("pms-collapsed-row")} onClick={toggle}>
                    <ParticipantAvatar p={collapsedTarget} size={32} />
                    <div className={cls("pms-collapsed-name")}>
                        {collapsedTarget.nickname}
                        {collapsedTarget.isHost && <span className={cls("pms-crown")} title={i18n.t('meetingUpcoming.host')}><IcCrown /></span>}
                        {collapsedTarget.isMe && <span className={cls("pms-me-pill")}>{i18n.t('race.me')}</span>}
                    </div>
                    <AttendanceBadge status={collapsedTarget.attendanceStatus} />
                    <LocationBadge sharing={!!collapsedTarget.locationSharing} />
                </div>
            )}

            {/* 펼침 전체 목록 */}
            {expanded && (
                <ul className={cls("pms-list")}>
                    {sorted.map(p => (
                        <li key={p.userNo} className={cls(`pms-row ${p.isMe ? 'me' : ''}`)}>
                            <ParticipantAvatar p={p} size={36} />
                            <div className={cls("pms-name")}>
                                {p.nickname}
                                {p.isHost && <span className={cls("pms-crown")} title={i18n.t('meetingUpcoming.host')}><IcCrown /></span>}
                                {p.isMe && <span className={cls("pms-me-pill")}>{i18n.t('race.me')}</span>}
                            </div>
                            <AttendanceBadge status={p.attendanceStatus} />
                            <LocationBadge sharing={!!p.locationSharing} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const ParticipantAvatar: React.FC<{ p: ParticipantRowInfo; size: number }> = ({ p, size }) => {
    const initial = p.nickname?.[0] || '?';
    return p.profileImgUrl ? (
        <img
            className={cls("pms-avatar-img")}
            src={p.profileImgUrl}
            alt={p.nickname}
            style={{ width: size, height: size }}
        />
    ) : (
        <div
            className={cls("pms-avatar-fallback")}
            style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
        >
            {initial}
        </div>
    );
};

const AttendanceBadge: React.FC<{ status?: string }> = ({ status }) => {
    const label = ATTENDANCE_LABEL[status || ''] || '미정';
    const tone =
        status === 'ATTENDING' ? 'attend' :
        status === 'NOT_ATTENDING' ? 'decline' :
        'pending';
    return <span className={cls(`pms-attend-badge ${tone}`)}>{label}</span>;
};

const LocationBadge: React.FC<{ sharing: boolean }> = ({ sharing }) => (
    <span
        className={cls(`pms-loc-badge ${sharing ? 'on' : 'off'}`)}
        title={sharing ? i18n.t('meetingUpcoming.locSharing') : i18n.t('meetingUpcoming.locSharingOff')}
    >
        {sharing ? <IcPin /> : <IcPinOff />}
    </span>
);

export default ParticipantMapSheet;
